import * as AT from '../actions/_types'
import { generateChatCompletion } from '../../services/openai-api'
import { logException } from '../../services/sentry'

/**
 * Generate AI response based on conversation context and current content
 * 
 * @param {Object} conversationContext - existing messages between the patient and the provider
 * @param {string} prompt - Prompt for the AI
 * @param {string} tone - Tone of the message
 * @returns {Function} - Thunk action that returns a Promise
 */
export const generateAIResponse = 
  (conversationContext, prompt, tone) => async (dispatch, getState) => {

    dispatch({ type: AT.AI_ASSISTANT_REQUEST })
    try {
  // Format messages for OpenAI's expected structure
      const messages = [
        { 
          role: "system", 
          content: "You are a helpful assistant aiding healthcare administrative staff who communicate with patients. Help draft professional, clear, and concise responses to patient inquiries." 
        }
      ]
      
      // Get only the last 10 messages from conversation context
      const lastTenMessages = Array.isArray(conversationContext) 
        ? conversationContext.slice(-10) 
        : conversationContext;
      
      // Construct user prompt with limited conversation context and current content
      const conversationContextString = JSON.stringify(lastTenMessages)
      const promptWithContext = `
You are drafting a response from healthcare administrative staff to a patient.

CONVERSATION HISTORY:
${conversationContextString}

TASK:
${prompt}

TONE INSTRUCTIONS:
Please write in a ${tone} tone while maintaining professionalism.

RESPONSE GUIDELINES:
- Be clear, concise, and empathetic
- Address the patient's concerns directly
- Provide any necessary information or next steps
- Maintain HIPAA compliance and appropriate privacy
- DO NOT GIVE MEDICAL ADVICE
- Keep responses brief

FORMAT:
Return ONLY the message text. Do not include subject lines, greetings, salutations, or signatures.
`.trim();
      
      // Add as single user prompt
      messages.push({ 
        role: "user", 
        content: promptWithContext 
      })
    
      // Make request to OpenAI API via our backend
      const generatedText = await generateChatCompletion(messages, {
        model: "gpt-4o-mini",
        temperature: 0.7,
        maxTokens: 500
      })
      
      // Dispatch success action
      dispatch({
        type: AT.AI_ASSISTANT_SUCCESS,
        generatedText
      })
      
      return generatedText
    } catch (error) {
      // Log the error
      logException(error)
      
      // Dispatch failure action
      dispatch({
        type: AT.AI_ASSISTANT_FAILURE,
        error: error.message || 'Failed to get AI suggestion'
      })
      
      throw error
    }
  } 

/**
 * Clear the generated text in the store
 * 
 * @returns {Object} - Action to clear the generated text
 */
export const clearGeneratedText = () => ({
  type: AT.AI_ASSISTANT_CLEAR
}) 



// WEBPACK FOOTER //
// ./src/state/actions/ai-assistant.js